import { useCallback } from "react"
import { useClient } from "urql"

import type { SoftReceivedPurchaseOrdersFormValues } from "../../../schemas"
import type { SoftReceivedLotReference } from "../types"
import { getSoftReceivedLotReferencesFromFormValues } from "../utils"

import type { GoodsReceipt, ProductTestListSuccess } from "@/graphql/codegen/graphql"
import { GetExistingProductTestListDocument } from "@/graphql/codegen/graphql"

interface UseValidateSoftReceivedLotNumbersReturn {
  validateSoftReceivedLotNumbers: (
    formValues: SoftReceivedPurchaseOrdersFormValues,
    goodsReceipt: GoodsReceipt
  ) => Promise<SoftReceivedLotReference[]>
}

export const useValidateSoftReceivedLotNumbers = (): UseValidateSoftReceivedLotNumbersReturn => {
  const client = useClient()

  const validateSoftReceivedLotNumbers = useCallback(
    async (
      formValues: SoftReceivedPurchaseOrdersFormValues,
      goodsReceipt: GoodsReceipt
    ): Promise<SoftReceivedLotReference[]> => {
      const lotReferences = getSoftReceivedLotReferencesFromFormValues(formValues, goodsReceipt)
      const invalidLots: SoftReceivedLotReference[] = []

      for (const lotReference of lotReferences) {
        const result = await client
          .query(GetExistingProductTestListDocument, {
            input: {
              filter: {
                productId: lotReference.product.productId,
                lotNumber: lotReference.lotNumber,
                batchNumber: lotReference.batchNumber,
              },
            },
          })
          .toPromise()

        if (result.data?.productTest.list.__typename === "ProductTestListSuccess") {
          const productTests = (result.data.productTest.list as ProductTestListSuccess).productTests

          if (productTests.length === 0) {
            invalidLots.push(lotReference)
          }
        }
      }

      return invalidLots
    },
    [client]
  )

  return {
    validateSoftReceivedLotNumbers,
  }
}
